import React from 'react';

interface BulletPointTextProps {
    text: string;
    className?: string;
}

/* ------------------------------------------------------------------ */
/*  Line‑level classification                                          */
/* ------------------------------------------------------------------ */

type LineType =
    | { kind: 'h1'; content: string }
    | { kind: 'h2'; content: string }
    | { kind: 'h3'; content: string }
    | { kind: 'header-colon'; header: string; rest: string }
    | { kind: 'bullet'; content: string }
    | { kind: 'numbered'; index: string; content: string }
    | { kind: 'paragraph'; content: string }
    | { kind: 'blank' };

/**
 * Strip emoji / symbol / pictograph characters from text.
 * Keeps all normal letters, numbers, punctuation, maths, currency, etc.
 */
function stripEmojis(str: string): string {
    return str.replace(/[\u{1F600}-\u{1F9FF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1FA00}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{E0020}-\u{E007F}]/gu, '').replace(/  +/g, ' ');
}

/**
 * Split inline numbered items embedded in a single line into separate lines.
 * e.g. "Header: 1) item one. 2) item two." → ["Header:", "1) item one.", "2) item two."]
 * Only triggers when ≥2 numbered patterns detected in one line.
 */
function splitInlineNumberedItems(line: string): string[] {
    // Count "N) " patterns (1-3 digit number + closing paren + space)
    const parenHits = line.match(/\d{1,3}\)\s+/g);
    if (parenHits && parenHits.length >= 2) {
        const parts = line.split(/\s+(?=\d{1,3}\)\s+)/);
        const cleaned = parts.map(p => p.trim()).filter(p => p.length > 0);
        if (cleaned.length >= 2) return cleaned;
    }
    // Also handle "N. " patterns when ≥2 occur (e.g. "1. item 2. item")
    const dotHits = line.match(/(?:^|\s)\d{1,3}\.\s+/g);
    if (dotHits && dotHits.length >= 2) {
        const parts = line.split(/\s+(?=\d{1,3}\.\s+)/);
        const cleaned = parts.map(p => p.trim()).filter(p => p.length > 0);
        if (cleaned.length >= 2) return cleaned;
    }
    return [line];
}

/** Try to detect and pretty‑format a JSON string. */
function tryParseJson(raw: string): object | null {
    const trimmed = raw.trim();
    if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
        try {
            return JSON.parse(trimmed);
        } catch {
            return null;
        }
    }
    return null;
}

/** Classify a single logical line. */
function classifyLine(line: string): LineType {
    const trimmed = line.trim();

    if (trimmed === '') return { kind: 'blank' };

    // Markdown headings
    if (/^###\s+(.+)/.test(trimmed)) return { kind: 'h3', content: trimmed.replace(/^###\s+/, '') };
    if (/^##\s+(.+)/.test(trimmed)) return { kind: 'h2', content: trimmed.replace(/^##\s+/, '') };
    if (/^#\s+(.+)/.test(trimmed)) return { kind: 'h1', content: trimmed.replace(/^#\s+/, '') };

    // Bold-only line = pseudo‑header  e.g. "**Data Quality**"
    const boldOnly = trimmed.match(/^\*\*(.+?)\*\*:?\s*$/);
    if (boldOnly) return { kind: 'h3', content: boldOnly[1] };

    // Bullet lines   -  •  *  ‣  ✓  ✔  ▸  ►  →
    // Match with or without space after marker (backend sometimes sends •Text)
    if (/^[-•‣✓✔▸►→]\s*(?=\S)/.test(trimmed) || /^\*\s+/.test(trimmed)) {
        const bulletContent = trimmed.replace(/^[-•‣✓✔▸►→]\s*/, '').replace(/^\*\s+/, '');

        // Promote to header if the bullet content is just "Some Label:" with
        // no body text — e.g. "• Key Findings:" should be a section header
        const standaloneHeader = bulletContent.match(/^([A-Z][A-Za-z0-9 &/,'()-]{2,58}):\s*$/);
        if (standaloneHeader) {
            return { kind: 'h3', content: standaloneHeader[1].trim() };
        }

        // Promote to header-colon if it's "• Score Interpretation: body..."
        const bulletColonMatch = bulletContent.match(/^([A-Z][A-Za-z0-9 &/,'()-]{2,58}):\s+(.+)/);
        if (bulletColonMatch) {
            const hWords = bulletColonMatch[1].trim().split(/\s+/);
            if (hWords.length >= 1 && hWords.length <= 6) {
                return { kind: 'header-colon', header: bulletColonMatch[1].trim(), rest: bulletColonMatch[2] };
            }
        }

        return { kind: 'bullet', content: bulletContent };
    }

    // Numbered list  "1." or "1)" or "(1)" or "(a)"
    const numMatch = trimmed.match(/^(?:(\d+)[.)]\s+|\((\d+|[a-z])\)\s+)/);
    if (numMatch) {
        const idx = numMatch[1] || numMatch[2];
        return { kind: 'numbered', index: idx, content: trimmed.replace(/^(?:\d+[.)]\s+|\(\d+\)\s+|\([a-z]\)\s+)/, '') };
    }

    // Standalone header ending with colon (no body text after it)
    // e.g. "Immediate Actions:" or "Next Steps:" as its own line → promote to h3
    const standaloneColonHeader = trimmed.match(/^([A-Z][A-Za-z0-9 &/,'()-]{2,58}):\s*$/);
    if (standaloneColonHeader) {
        const hWords = standaloneColonHeader[1].trim().split(/\s+/);
        if (hWords.length >= 1 && hWords.length <= 8) {
            return { kind: 'h3', content: standaloneColonHeader[1].trim() };
        }
    }

    // Header‑colon pattern  "Some Header: body text that follows"
    // Keep strict: header part ≤60 chars, at least 3 alpha chars, 2–6 words
    const colonMatch = trimmed.match(/^([A-Z][A-Za-z0-9 &/,'()-]{2,58}):\s+(.+)/);
    if (colonMatch) {
        const headerWords = colonMatch[1].trim().split(/\s+/);
        // Only treat as header if 1-6 words and starts with uppercase
        if (headerWords.length >= 1 && headerWords.length <= 6) {
            return { kind: 'header-colon', header: colonMatch[1].trim(), rest: colonMatch[2] };
        }
    }

    return { kind: 'paragraph', content: trimmed };
}

/* ------------------------------------------------------------------ */
/*  Inline formatting helpers                                          */
/* ------------------------------------------------------------------ */

/** Render inline markdown: **bold**, *italic*, `code` */
function renderInline(text: string): React.ReactNode[] {
    const nodes: React.ReactNode[] = [];
    // Order matters: bold first, then italic, then code
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
        // Push text before the match
        if (match.index > lastIndex) {
            nodes.push(text.slice(lastIndex, match.index));
        }

        if (match[2]) {
            // **bold**
            nodes.push(<strong key={match.index} className="font-semibold">{match[2]}</strong>);
        } else if (match[3]) {
            // *italic*
            nodes.push(<em key={match.index}>{match[3]}</em>);
        } else if (match[4]) {
            // `code`
            nodes.push(
                <code key={match.index} className="text-xs bg-black/5 px-1.5 py-0.5 rounded font-mono">
                    {match[4]}
                </code>
            );
        }

        lastIndex = match.index + match[0].length;
    }

    // Remaining text after last match
    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }

    return nodes.length > 0 ? nodes : [text];
}

/* ------------------------------------------------------------------ */
/*  JSON renderer                                                      */
/* ------------------------------------------------------------------ */

function JsonBlock({ data }: { data: object }) {
    return (
        <div className="my-3 rounded-lg border border-gray-200 bg-gray-50 overflow-x-auto">
            <pre className="p-4 text-xs font-mono text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                {JSON.stringify(data, null, 2)}
            </pre>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/**
 * Smart rich-text renderer for backend report data.
 *
 * Handles:
 * - Newlines (\n) as real line breaks
 * - Markdown headings (# / ## / ###)
 * - Bold-only lines as section headers
 * - "Header: body" colon patterns
 * - Bullet lists (-, •, *, etc.)
 * - Numbered lists (1. / 1) / (a))
 * - Inline **bold**, *italic*, `code`
 * - JSON detection and pretty-print
 */
export function BulletPointText({ text, className = '' }: BulletPointTextProps) {
    if (!text) return null;

    // ── 1. Whole-text JSON detection ──────────────────────────────
    const jsonObj = tryParseJson(text);
    if (jsonObj) {
        return (
            <div className={`notranslate ${className}`} translate="no">
                <JsonBlock data={jsonObj} />
            </div>
        );
    }

    // ── 2. Strip emojis from backend text ────────────────────────
    let fixedText = stripEmojis(text)

    // ── 2b. Fix known broken percentage patterns ─────────────────
    fixedText = fixedText
        .replace(/\(\s*\.\s*\n\s*(\d+%?\)\s*)/g, '(0.$1')
        .replace(/\(\s*\.\s*(\d+%?\)\s*)/g, '(0.$1')
        .replace(/\(\s*\.\s*\n?\s*(\d+)%\s*\)/g, '(0.$1%)')
        .replace(/\(\s*\.\s*(\d+)%\s*\)/g, '(0.$1%)');

    // ── 3. Split on real newlines & merge orphan bullet markers ─
    //   Backend sometimes sends "•\nActual text" — a lone bullet on
    //   its own line followed by the content on the next line. Merge
    //   these so the bullet marker stays with its content.
    const splitLines = fixedText.split('\n');
    const rawLines: string[] = [];
    for (let li = 0; li < splitLines.length; li++) {
        const trimmed = splitLines[li].trim();
        // A line that is ONLY a bullet/dash marker with no real content
        if (/^[-•*‣✓✔▸►→]$/.test(trimmed) && li + 1 < splitLines.length) {
            // Prepend the marker to the next line
            rawLines.push(`${trimmed} ${splitLines[li + 1].trim()}`);
            li++; // skip the next line since we merged it
        } else {
            // Normalize no-space bullets: "•Text" → "• Text"
            const normalized = trimmed.replace(/^([-•‣✓✔▸►→])(?=\S)/, '$1 ');
            rawLines.push(normalized);
        }
    }

    // ── 3b. Split inline numbered items into separate lines ──────
    //   Backend sometimes sends "header: 1) item 2) item 3) item"
    //   as a single paragraph. Expand into separate lines so each
    //   numbered item renders as a proper bullet point.
    const expandedLines = rawLines.flatMap(splitInlineNumberedItems);

    const classified = expandedLines.map(classifyLine);

    // ── 4. Render lines into React elements ──────────────────────
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < classified.length) {
        const line = classified[i];

        // Attempt multi-line JSON detection: if a paragraph starts with { or [
        if (line.kind === 'paragraph' && /^\s*[{[]/.test(line.content)) {
            let jsonCandidate = line.content;
            let j = i + 1;
            let foundJson = false;
            // Collect lines until we close the JSON or hit a clear non-JSON line
            while (j < classified.length) {
                const next = classified[j];
                if (next.kind === 'paragraph' || next.kind === 'blank') {
                    jsonCandidate += '\n' + (next.kind === 'blank' ? '' : (next as { kind: 'paragraph'; content: string }).content);
                    j++;
                    const parsed = tryParseJson(jsonCandidate);
                    if (parsed) {
                        elements.push(<JsonBlock key={`json-${i}`} data={parsed} />);
                        i = j;
                        foundJson = true;
                        break;
                    }
                } else {
                    break;
                }
            }
            if (foundJson) continue; // successfully consumed JSON
            // if not JSON, fall through to normal rendering
        }

        // ── Render by kind ───────────────────────────────────────
        switch (line.kind) {
            case 'blank':
                // Collapse consecutive blanks; emit spacer if non-trivial gap
                if (elements.length > 0) {
                    elements.push(<div key={`sp-${i}`} className="h-2" />);
                }
                break;

            case 'h1':
                elements.push(
                    <h3 key={`h1-${i}`} className="text-lg font-bold mt-4 mb-2 first:mt-0">
                        {renderInline(line.content)}
                    </h3>
                );
                break;

            case 'h2':
                elements.push(
                    <h4 key={`h2-${i}`} className="text-base font-bold mt-3 mb-1.5 first:mt-0">
                        {renderInline(line.content)}
                    </h4>
                );
                break;

            case 'h3':
                elements.push(
                    <h5 key={`h3-${i}`} className="text-sm font-bold mt-3 mb-1 first:mt-0">
                        {renderInline(line.content)}
                    </h5>
                );
                break;

            case 'header-colon':
                elements.push(
                    <div key={`hc-${i}`} className="mt-3 first:mt-0">
                        <span className="font-semibold">{line.header}:</span>{' '}
                        <span>{renderInline(line.rest)}</span>
                    </div>
                );
                break;

            case 'bullet': {
                // Collect consecutive bullets into a list
                const bullets: string[] = [line.content];
                while (i + 1 < classified.length && classified[i + 1].kind === 'bullet') {
                    i++;
                    bullets.push((classified[i] as { kind: 'bullet'; content: string }).content);
                }
                elements.push(
                    <ul key={`ul-${i}`} className="space-y-1.5 my-2">
                        {bullets.map((b, bi) => (
                            <li key={bi} className="flex items-start gap-2">
                                <span className="mt-1 min-w-[8px] flex-shrink-0" style={{ color: '#D31145' }}>&#8226;</span>
                                <span className="flex-1">{renderInline(b)}</span>
                            </li>
                        ))}
                    </ul>
                );
                break;
            }

            case 'numbered': {
                // Collect consecutive numbered items — render as bullets for consistency
                const items: { idx: string; content: string }[] = [{ idx: line.index, content: line.content }];
                while (i + 1 < classified.length && classified[i + 1].kind === 'numbered') {
                    i++;
                    const next = classified[i] as { kind: 'numbered'; index: string; content: string };
                    items.push({ idx: next.index, content: next.content });
                }
                elements.push(
                    <ul key={`ol-${i}`} className="space-y-1.5 my-2">
                        {items.map((item, ni) => (
                            <li key={ni} className="flex items-start gap-2">
                                <span className="mt-1 min-w-[8px] flex-shrink-0" style={{ color: '#D31145' }}>&#8226;</span>
                                <span className="flex-1">{renderInline(item.content)}</span>
                            </li>
                        ))}
                    </ul>
                );
                break;
            }

            case 'paragraph':
                elements.push(
                    <p key={`p-${i}`} className="leading-relaxed">
                        {renderInline(line.content)}
                    </p>
                );
                break;

            default:
                break;
        }

        i++;
    }

    // ── 5. Fallback: if nothing was parsed, show raw text with line breaks
    if (elements.length === 0) {
        return (
            <div className={`notranslate ${className}`} translate="no">
                <p className="leading-relaxed whitespace-pre-line">{fixedText}</p>
            </div>
        );
    }

    return <div className={`notranslate ${className}`} translate="no">{elements}</div>;
}